import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppStore } from "../../store/useAppStore";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Logo } from "../ui/logo";

export function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setToken } = useAppStore();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      // Determine which provider this callback is for. The configured redirect is
      // /auth/callback (no provider in the path), so prefer the value stashed when
      // the flow was initiated; fall back to an /auth/oauth/{provider}/callback path.
      // A wrong guess fails the backend's signed-state check cleanly rather than
      // authenticating against the wrong provider.
      let provider = sessionStorage.getItem("oauth-provider") || "";
      const pathParts = window.location.pathname.split("/");
      if (!provider && pathParts.includes("oauth")) {
        provider = pathParts[pathParts.indexOf("oauth") + 1] || "";
      }
      if (!provider) {
        provider = "github"; // last-resort default
      }
      sessionStorage.removeItem("oauth-provider"); // consumed; don't leave it around

      if (error) {
        setStatus("error");
        setError(`OAuth error: ${error}`);
        return;
      }

      if (!code || !state) {
        setStatus("error");
        setError("Missing authorization code or state parameter");
        return;
      }

      try {
        // Send the code to our backend for token exchange (consistent with VCS integration)
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:8003/api"}/auth/oauth/${provider}/callback?code=${code}&state=${state}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "OAuth authentication failed");
        }

        const data = await response.json();

        // Store the JWT token and user data
        if (data.token) {
          localStorage.setItem("avahana-token", data.token);
          setToken(data.token);
          setUser(data.user);
          setStatus("success");

          // Redirect to dashboard after a brief success message
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
        } else {
          throw new Error("No token received");
        }
      } catch (err: any) {
        setStatus("error");
        setError(err.message || "OAuth authentication failed");
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, setUser, setToken]);

  const handleRetry = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <Logo size="lg" />
          </div>
          <CardTitle className="text-h2">
            {status === "loading" && "Completing Sign In..."}
            {status === "success" && "Sign In Successful!"}
            {status === "error" && "Sign In Failed"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === "loading" && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
              <p className="text-muted-foreground">
                Please wait while we complete your authentication...
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4">
              <div className="text-success">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-muted-foreground">
                You have been successfully signed in. Redirecting to
                dashboard...
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <div className="text-destructive">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <p className="text-destructive text-sm">{error}</p>
              <button
                onClick={handleRetry}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
